// Infrastructure imports
import {
  ProductPouchDBAdapter,
  WarehousePouchDBAdapter,
  MovementPouchDBAdapter,
  LocationPouchDBAdapter,
  StockPouchDBAdapter,
  AuthPouchDBAdapter,
  ProductVariantPouchDBAdapter
} from '@infrastructure/db/adapters';

// Domain interfaces
import type {
  IRepository,
  IProductRepository,
  IWarehouseRepository,
  IMovementRepository,
  IStockService,
  IEventEmitter,
  IBarcodeService
} from '@core/domain/interfaces';

// Domain services
import { ProductManagementService } from '@core/domain/services/ProductManagementService';
import { WarehouseManagementService } from '@core/domain/services/WarehouseManagementService';
import { BarcodeService } from '@core/domain/services/BarcodeService';
import { ValuationService } from '@core/domain/services/ValuationService';
import { StockService } from '@core/domain/services/StockService';

// Auth
import { AuthService } from '../auth/services/AuthService';

// Events
import { EventEmitter } from '../shared/events/EventEmitter';

import type { CreateProductUseCase } from '../application/use-cases/CreateProductUseCase';
import CreateProductUseCaseImpl from '../application/use-cases/CreateProductUseCase';
import type { UpdateProductUseCase } from '../application/use-cases/UpdateProductUseCase';
import UpdateProductUseCaseImpl from '../application/use-cases/UpdateProductUseCase';
import type { FindProductsUseCase } from '../application/use-cases/FindProductsUseCase';
import FindProductsUseCaseImpl from '../application/use-cases/FindProductsUseCase';
import RegisterMovementUseCaseImpl from '../application/use-cases/RegisterMovementUseCase';
import RegisterSaleUseCaseImpl from '../application/use-cases/RegisterSaleUseCase';
import type { FindSalesUseCase } from '../application/use-cases/FindSalesUseCase';
import FindSalesUseCaseImpl from '../application/use-cases/FindSalesUseCase';
import type { FindWarehousesUseCase } from '../application/use-cases/FindWarehousesUseCase';
import FindWarehousesUseCaseImpl from '../application/use-cases/FindWarehousesUseCase';
import CreateWarehouseUseCaseImpl from '../application/use-cases/CreateWarehouseUseCase';
import UpdateWarehouseUseCaseImpl from '../application/use-cases/UpdateWarehouseUseCase';
import GetInventorySnapshotUseCaseImpl from '../application/use-cases/GetInventorySnapshotUseCase';
import FindMovementsUseCaseImpl from '../application/use-cases/FindMovementsUseCase';


export class Container {
  private static instance: Container;
  private services: Map<string, any> = new Map();

  private constructor() {
    this.initializeServices();
  }

  public static getInstance(): Container {
    if (!Container.instance) {
      Container.instance = new Container();
    }
    return Container.instance;
  }

  private initializeServices(): void {
    // Event Emitter
    const eventEmitter = new EventEmitter();
    this.services.set('EventEmitter', eventEmitter);
    
    // Database adapters
    const dbName = 'inventory_db';
    this.services.set('ProductRepository', new ProductPouchDBAdapter(dbName));
    this.services.set('WarehouseRepository', new WarehousePouchDBAdapter(dbName));
    this.services.set('MovementRepository', new MovementPouchDBAdapter(dbName));
    this.services.set('LocationRepository', new LocationPouchDBAdapter(dbName));
    this.services.set('StockRepository', new StockPouchDBAdapter(dbName));
    this.services.set('ProductVariantRepository', new ProductVariantPouchDBAdapter(dbName));

    // Auth
    const jwtSecret = (import.meta as any).env?.VITE_JWT_SECRET ?? 'dev-secret-key';
    this.services.set('AuthRepository', new AuthPouchDBAdapter(dbName));
    this.services.set('AuthService', new AuthService(
      this.get<AuthPouchDBAdapter>('AuthRepository'),
      jwtSecret
    ));

    // Domain services
    this.services.set('BarcodeService', new BarcodeService());
    
    // Stock service
    const inventoryEventEmitter = {
      emitStockChanged: (p: string, w: string) => eventEmitter.emit('stock:changed', { p, w }),
      emitLowStock: (p: string, w: string, s: any) => eventEmitter.emit('stock:low', { p, w, s }),
      emitStockOut: (p: string, w: string) => eventEmitter.emit('stock:out', { p, w }),
      onStockChanged: (h: (p: string, w: string) => void) =>
        eventEmitter.on('stock:changed', (pl: any) => h(pl.p, pl.w)),
      onLowStock: (h: (p: string, w: string, s: any) => void) =>
        eventEmitter.on('stock:low', (pl: any) => h(pl.p, pl.w, pl.s)),
      onStockOut: (h: (p: string, w: string) => void) =>
        eventEmitter.on('stock:out', (pl: any) => h(pl.p, pl.w)),
    };
    const movementRepo = this.get<MovementPouchDBAdapter>('MovementRepository');
    this.services.set('StockService', new StockService(
      async (productId: string, warehouseId?: string) => {
        const all = await movementRepo.findByProductId(productId);
        return (warehouseId ? all.filter(m => m.warehouseId === warehouseId) : all) as any;
      },
      inventoryEventEmitter
    ));

    this.services.set('ProductManagementService', new ProductManagementService(
      this.get<ProductPouchDBAdapter>('ProductRepository'),
      this.get<IBarcodeService>('BarcodeService'),
      this.get<MovementPouchDBAdapter>('MovementRepository')
    ));
    
    this.services.set('WarehouseManagementService', new WarehouseManagementService(
      this.get<any>('LocationRepository'),
      this.get<any>('StockRepository'),
      this.get<MovementPouchDBAdapter>('MovementRepository')
    ));
    
    // this.services.set('ValuationService', new ValuationService(
    //   this.get<IProductRepository>('ProductRepository'),
    //   this.get<IMovementRepository>('MovementRepository')
    // ));

    // // Use cases
    this.services.set('CreateProductUseCase', new CreateProductUseCaseImpl(
      this.get<ProductManagementService>('ProductManagementService'),
      // this.get<BarcodeService>('BarcodeService')
    ));

    this.services.set('UpdateProductUseCase', new UpdateProductUseCaseImpl(
      this.get<ProductManagementService>('ProductManagementService'),
    ));

    this.services.set('FindProductsUseCase', new FindProductsUseCaseImpl(
      this.get<ProductPouchDBAdapter>('ProductRepository'),
    ));

    this.services.set('FindWarehousesUseCase', new FindWarehousesUseCaseImpl(
      this.get<WarehousePouchDBAdapter>('WarehouseRepository'),
    ));

    this.services.set('CreateWarehouseUseCase', new CreateWarehouseUseCaseImpl(
      this.get<WarehousePouchDBAdapter>('WarehouseRepository'),
    ));

    this.services.set('UpdateWarehouseUseCase', new UpdateWarehouseUseCaseImpl(
      this.get<WarehousePouchDBAdapter>('WarehouseRepository'),
    ));

    this.services.set('RegisterMovementUseCase', new RegisterMovementUseCaseImpl(
      this.get<MovementPouchDBAdapter>('MovementRepository'),
      this.get<StockService>('StockService'),
    ));

    // Sales use cases
    this.services.set('RegisterSaleUseCase', new RegisterSaleUseCaseImpl(
      this.get<ProductPouchDBAdapter>('ProductRepository'),
      this.get<MovementPouchDBAdapter>('MovementRepository'),
      this.get<StockService>('StockService'),
    ));

    this.services.set('FindSalesUseCase', new FindSalesUseCaseImpl(
      this.get<any>('MovementRepository'),
      this.get<ProductPouchDBAdapter>('ProductRepository')
    ));

    this.services.set('GetInventorySnapshotUseCase', new GetInventorySnapshotUseCaseImpl(
      this.get<ProductPouchDBAdapter>('ProductRepository'),
      this.get<WarehousePouchDBAdapter>('WarehouseRepository'),
      this.get<StockService>('StockService'),
    ));

    this.services.set('FindMovementsUseCase', new FindMovementsUseCaseImpl(
      this.get<any>('MovementRepository'),
      this.get<ProductPouchDBAdapter>('ProductRepository'),
      this.get<WarehousePouchDBAdapter>('WarehouseRepository'),
    ));
  }

  public get<T>(serviceName: string): T {
    const service = this.services.get(serviceName);
    if (!service) {
      throw new Error(`Service ${serviceName} not found in container`);
    }
    // console.log(`Retrieved service ${serviceName}:`, service);
    return service as T;
  }

  public register<T>(serviceName: string, service: T): void {
    this.services.set(serviceName, service);
  }
}
